import EditIcon from 'ui/elements/icons/EditIcon';
import React, { useState } from 'react';
import { Update } from 'types/content';
import Chip from 'ui/elements/Chip';
import ArchiveIcon from 'ui/elements/icons/ArchiveIcon';
import UnarchiveIcon from 'ui/elements/icons/UnarchiveIcon';
import PinIcon from 'ui/elements/icons/PinIcon';
import ConfirmDialog from 'ui/modules/dialogs/ConfirmDialog';
import useDialogHandler from 'hooks/useDialogHandler';
import Button from 'ui/elements/buttons/Button';
import { ICompany } from 'types/company';
import { CircularProgress, Fade } from '@mui/material';
import EyeIcon from 'ui/elements/icons/EyeIcon';
import useFollowCompany from 'domain/companies/Follow/useFollowCompany';
import UserIcon from 'ui/elements/icons/UserIcon';
import UserProfileDialog from 'domain/users/UserProfile/UserProfileDialog';
import GraphIcon from 'ui/elements/icons/GraphIcon';
import UpdateStatsDialog from '../UpdateStatsDialog';
import TrashIcon from 'ui/elements/icons/TrashIcon';
import { Auth0User } from 'types';
import { useAuth0 } from '@auth0/auth0-react';
import { getAdminRights } from 'auth/util';
import { UpdateAPI } from 'apis/CompanyAPI/content/UpdateAPI';
import useNotify from 'hooks/useNotify';
import useLazyResource from 'util/resource/useLazyResource';
import PopMenu from 'ui/modules/PopMenu';
import CompanyProfileDialog from 'domain/companies/ProfileDialog/CompanyProfileDialog';
import useBackoffice from 'hooks/useBackoffice';
import CheckmarkBoxIcon from 'ui/elements/icons/CheckmarkBoxIcon';
import UnfollowDialog from 'domain/companies/Follow/UnfollowDialog';
import MinusIcon from 'ui/elements/icons/MinusIcon';
import TimerIcon from 'ui/elements/icons/TimerIcon';
import PlusBoxIcon from 'ui/elements/icons/PlusBoxIcon';
import { bluePlanetTheme } from 'ui/theme';
import CopyLinkIcon from 'ui/elements/icons/CopyLinkIcon';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import CheckMarkCircleIcon from 'ui/elements/icons/CheckmarkCircleIcon';
import UserConversationDialog from 'domain/conversations/UserConversationDialog';
import RepostUpdateDialog from '../../RepostUpdateDialog';
import UpdateIcon from 'ui/elements/icons/UpdateIcon';
import { getOrUndefined } from 'util/resource';
import { usePostableSources } from 'apis/CompanyAPI/users/usePostableSources';
import SendIcon from 'ui/elements/icons/SendIcon';

interface ContainerProps {
  update: Update;
  company: ICompany;
  updateUrl: string;
  location: { type: 'company' | 'community'; slug: string };
  api: UpdateAPI;
  authorId?: string;
  onEdit?: () => void;
  canEditPost?: boolean;
  onArchive?: (contentId: string) => void;
  onDelete?: () => void;
  setPinned: (isPinned: boolean) => void;
  isPinned: boolean;
  canPinToFeed?: boolean;
  trackingKey?: string;
  isAtProfile: boolean;
}

export default function CompanyUpdateMenu(props: ContainerProps) {
  const { update, isPinned, setPinned } = props;
  const [isDeleting, setDeleting] = useState(false);

  const [isArchived, setArchived] = useState<boolean>(update.archived || false);
  const notify = useNotify();

  const [isPinDialogOpen, setPinDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [isShareUpdateDialogOpen, setShareUpdateDialogOpen] = useState(false);

  const [togglePin] = useLazyResource((isPinned: boolean) => props.api.togglePin(props.update.id, isPinned), {
    onSuccess: () => {
      setPinned(!isPinned);
      notify('success', `Update has been ${isPinned ? 'pinned' : 'unpinned'} `);
    },
    onFailure: () => notify('error', `Could not ${isPinned ? 'pin' : 'unpin'} update. Please try again later.`),
  });
  const [onArchive] = useLazyResource(
    (isArchived: boolean) => (!isArchived ? props.api.archive(props.update.id) : props.api.unArchive(props.update.id)),
    {
      onSuccess: () => {
        setArchived(!isArchived);
        notify('success', `Update has been ${isArchived ? 'un' : ''}archived`);
      },
      onFailure: () => notify('error', `Could not ${isArchived ? 'un' : ''}archive update. Please try again later.`),
    },
  );

  const onDeleteContent = async () => {
    try {
      setDeleting(true);
      await props.api.delete(props.update.id);
      props.onDelete?.();
      notify('success', 'Update deleted');
    } catch {
      setDeleting(false);
      notify('error', 'Failed to delete update');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const [userDialogIsOpen, setUserDialogIsOpen] = useState(false);
  const [companyProfileDialogIsOpen, setCompanyProfileDialogIsOpen] = useState(false);
  const { user }: { user?: Auth0User } = useAuth0();
  const adminRights = getAdminRights(user);
  const backoffice = useBackoffice();
  const canAccessBackoffice = adminRights.includes('backoffice');
  const [updateStatsDialogIsOpen, setUpdateStatsDialogIsOpen] = useState(false);

  const [isHoveringPending, setHoveringPending] = useState(false);
  const unfollowDialogHandler = useDialogHandler();

  const { copy: copyUpdateLink, showCopiedSuccess: showUpdateLinkCopiedSuccess } = useCopyToClipboard(props.updateUrl);
  const [repostDialogIsOpen, setRepostDialgIsOpen] = useState(false);
  const { resource: postableSourcesResource } = usePostableSources();
  const postableFeeds = getOrUndefined(postableSourcesResource)?.postableFeeds ?? [];

  const {
    onFollow,
    onUnfollow,
    isUnfollowing,
    isCompanyRolesFetched,
    isFollowingCompany,
    isPendingFollowerRequest,
    isSendingFollowRequest,
    hasOtherRoleThanFollower,
    pendingStateIsDisabled,
    setPendingStateIsDisabled,
  } = useFollowCompany(props.company);

  if (!isCompanyRolesFetched) {
    return null;
  }

  const shouldRenderFollowMenuItem = !hasOtherRoleThanFollower;

  const followMenuItem = isFollowingCompany
    ? {
        text: 'Following',
        icon: <CheckmarkBoxIcon strokeWidth={1} />,
        onClick: unfollowDialogHandler.open,
      }
    : isPendingFollowerRequest
      ? {
          onMouseEnter: () => setHoveringPending(true),
          onMouseLeave: () => {
            setPendingStateIsDisabled(false);
            setHoveringPending(false);
          },
          text: isHoveringPending && !pendingStateIsDisabled ? 'Withdraw' : 'Waiting for approval',
          icon: isHoveringPending ? <MinusIcon /> : <TimerIcon strokeWidth={1} />,
          onClick: unfollowDialogHandler.open,
        }
      : {
          text: 'Follow',
          icon: isSendingFollowRequest ? (
            <Fade in={isSendingFollowRequest} unmountOnExit exit={false}>
              <CircularProgress disableShrink size={17} color="inherit" />
            </Fade>
          ) : (
            <PlusBoxIcon />
          ),
          onClick: onFollow,
        };

  return (
    <>
      {isArchived && <Chip color="orange-light" label="Archived" className="u-quarter-spacing-left" />}
      <PopMenu
        items={[
          {
            isVisible: shouldRenderFollowMenuItem,
            ...followMenuItem,
          },
          {
            isVisible: !props.isAtProfile,
            text: 'View company profile',
            onClick: () => setCompanyProfileDialogIsOpen(true),
            icon: <EyeIcon />,
          },
          {
            isVisible: true,
            text: 'Copy link to post',
            onClick: copyUpdateLink,
            icon: showUpdateLinkCopiedSuccess ? <CheckMarkCircleIcon color="green" /> : <CopyLinkIcon />,
          },
          {
            isVisible: true,
            text: 'Send link via chat',
            onClick: () => setShareUpdateDialogOpen(true),
            icon: <SendIcon />,
          },
          {
            isVisible: postableFeeds.length > 0,
            text: 'Share in new post',
            onClick: () => setRepostDialgIsOpen(true),
            icon: <UpdateIcon />,
          },
          {
            isVisible: !!props.authorId,
            text: 'View author profile',
            onClick: () => setUserDialogIsOpen(true),
            icon: <UserIcon />,
          },
          {
            isVisible: props.canEditPost && props.canPinToFeed,
            text: isPinned ? 'Unpin' : 'Pin to top',
            icon: <PinIcon />,
            onClick: () => setPinDialogOpen(true),
          },
          {
            isVisible: props.canEditPost && props.onEdit,
            text: 'Edit',
            onClick: props.onEdit,
            icon: <EditIcon />,
          },
          {
            isVisible: props.canEditPost,
            text: isArchived ? 'Unarchive' : 'Archive',
            icon: isArchived ? <UnarchiveIcon /> : <ArchiveIcon />,
            onClick: () => onArchive(isArchived),
          },

          {
            isVisible: backoffice.adminRights.includes('read-everything') && backoffice.showBackofficeInfoOnProfiles,
            text: 'View stats',
            icon: <GraphIcon />,

            onClick: () => setUpdateStatsDialogIsOpen(true),
            backgroundColor: bluePlanetTheme.bluePlanetPalette.yellow.medium,
          },
          {
            isVisible: props.onDelete && canAccessBackoffice,
            text: 'Delete',
            icon: <TrashIcon />,
            onClick: () => setDeleteDialogOpen(true),
          },
        ].filter(item => item.isVisible)}
      />
      <ConfirmDialog
        isOpen={isPinDialogOpen}
        onClose={() => setPinDialogOpen(false)}
        title={props.isPinned ? 'Unpin update' : 'Pin to top'}
        confirmButton={
          <Button kind="primary" onClick={() => togglePin(!isPinned)}>
            {props.isPinned ? 'Unpin' : 'Pin'}
          </Button>
        }
      >
        {props.isPinned
          ? 'The update will no longer appear at the top of the feed.'
          : 'The update will appear at the top of the feed. You can unpin it whenever you like.'}
      </ConfirmDialog>
      {props.authorId && (
        <UserProfileDialog
          isOpen={userDialogIsOpen}
          cwUserId={props.authorId}
          onClose={() => setUserDialogIsOpen(false)}
        />
      )}

      {updateStatsDialogIsOpen && (
        <UpdateStatsDialog updateId={update.id} onClose={() => setUpdateStatsDialogIsOpen(false)} />
      )}
      {companyProfileDialogIsOpen && (
        <CompanyProfileDialog
          analyticsContext="community-updates"
          companyId={props.company.id}
          analyticsCommunityId="none"
          initialTab="profile"
          onClose={() => setCompanyProfileDialogIsOpen(false)}
        />
      )}

      <UnfollowDialog
        dialogHandler={unfollowDialogHandler}
        onUnfollow={onUnfollow}
        isUnfollowing={isUnfollowing}
        companyName={props.company.name}
        requestIsPending={isPendingFollowerRequest}
      />
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="You are about to delete this post"
        confirmButton={
          <Button color="red" kind="primary" onClick={onDeleteContent} isLoading={isDeleting}>
            Yes, delete
          </Button>
        }
        dismissLabel="No, cancel"
      >
        <p className="u-content-spacing-bottom">It will no longer be visible anywhere on the platform.</p>
        <p>Are you sure?</p>
      </ConfirmDialog>
      {isShareUpdateDialogOpen && (
        <UserConversationDialog
          conversation={{ type: 'new' }}
          closeConversation={() => setShareUpdateDialogOpen(false)}
          sharedContent={{
            type: 'update',
            updateId: props.update.id,
          }}
        />
      )}
      <RepostUpdateDialog
        isOpen={repostDialogIsOpen}
        onClose={() => setRepostDialgIsOpen(false)}
        embeddedUpdate={{ updateId: update.id, location: { type: props.location.type, slug: props.location.slug } }}
      />
    </>
  );
}
